import { useState } from 'react'
import { FlowMeterSettings } from './charts/FlowMeterChart'
import { SensorProduct } from '../common/enums'
import { determinePrecision, formatNumber, formatReadingTimestamp } from '../common/utils'
import { useSensorConfigs } from '../hooks/useSensorConfigs'

const DEFAULT_FIELD_SIZE = 100

interface Props {
  sigfoxId: string
  sensorPort: string
  sensorProduct: SensorProduct
  sensorMetadata: any
  currentRate: number
  lastReading: number
  totalVolume: number
  totalVolumeInAcreFoot: number
  settings: FlowMeterSettings
}

export function FlowMeterChartSubHeader({
  sigfoxId,
  sensorPort,
  sensorProduct,
  sensorMetadata,
  totalVolume,
  totalVolumeInAcreFoot,
  currentRate,
  lastReading,
  settings,
}: Props) {
  const [fieldSize, setFieldSize] = useState(DEFAULT_FIELD_SIZE)

  // React Query rather than a raw effect, so this is keyed and deduplicated
  // instead of refiring on every remount of the chart list above. `enabled`
  // also stops the hook falling back to a Date.now() timestamp, which would
  // change the query key on every render.
  const timestamp = Math.floor(lastReading)
  const { data: sensorConfigResponse } = useSensorConfigs(sigfoxId, timestamp, false, !!sigfoxId && timestamp > 0)

  const { conversionScale, conversionOffset } = (sensorConfigResponse as any)?.data?.[sensorPort] ?? {}

  // A port need not report every category, so these lookups can come back
  // empty — the labels below fall back rather than assuming a match.
  const findConversions = (longName: string) =>
    sensorMetadata?.find((metadata: any) => metadata?.units?.category?.longName === longName)?.units?.conversions ?? null

  const volumeConversions = findConversions('Volume')
  const volumeRateConversions = findConversions('Volume Rate')

  const ricSensorInfo = `${sensorPort}: ${sensorProduct}`

  function calculateWaterDepth() {
    if (fieldSize === 0 || isNaN(fieldSize)) {
      return '-'
    }

    const waterDepthInInches = (totalVolumeInAcreFoot / fieldSize) * 12
    let precision = determinePrecision(waterDepthInInches, 3)
    precision = precision < settings.volume.precision ? settings.volume.precision : precision
    return formatNumber(waterDepthInInches, precision) + ' in'
  }

  return (
    <div className={tailwind.chartSubHeader}>
      <table className={tailwind.table}>
        <tbody>
          <tr className={tailwind.topTableRow}>
            <td>{ricSensorInfo}</td>
            <td>{formatReadingTimestamp(lastReading)}</td>
            <td>{`Rate: ${formatNumber(currentRate, settings.volumeRate.precision)} ${
              volumeRateConversions === null
                ? 'acre-ft'
                : volumeRateConversions.abbreviations[settings.volumeRate.unitId]
            }`}</td>
            <td>
              <div className="flex flex-row">
                {`Totals: ${formatNumber(totalVolume, settings.totalVolume.precision)} ${
                  volumeConversions === null ? 'gal' : volumeConversions.abbreviations[settings.totalVolume.unitId]
                }`}
              </div>
            </td>
          </tr>
          <tr className="text-sm">
            <td>{`K-Value: ${conversionScale ?? ''}`}</td>
            <td>{`Diameter: ${conversionOffset ?? ''}`}</td>
            <td className="pr-10">
              <div className="flex flex-row">
                <input
                  className={tailwind.inputText}
                  type="number"
                  id="field-size"
                  placeholder={'field size'}
                  value={fieldSize}
                  onChange={(e) => {
                    let value = parseInt(e.target.value)
                    if (value < 0) {
                      value = 0
                    }
                    setFieldSize(value)
                  }}
                />
                <div className="pt-0.5 pl-3">acres</div>
              </div>
            </td>
            <td>
              <div className="flex flex-row">{`Depth: ${calculateWaterDepth()}`}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const tailwind = {
  chartSubHeader: 'text-black text-l font-regular bg-[#DDD] pl-4 pr-4 w-full',

  table: 'table-fixed w-full',

  topTableRow: 'h-8 text-sm',

  inputText:
    'appearance-none border border-red-500 rounded w-full pt-0.5 px-1 mb-1 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer w-20',

  inputDropdown:
    'appearance-none border border-red-500 rounded w-full my-2 mr-3 py-2 px-3 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer',

  lockIcon: 'w-10 pt-2 pl-3 cursor-pointer',
}
